import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import UserService from '../firebase/users.service';

// Async thunks
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const users = await UserService.getAllUsers();
      return users;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch users');
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'users/updateUserStatus',
  async ({ userId, status }, { rejectWithValue }) => {
    try {
      await UserService.updateUser(userId, { status });
      return { userId, status };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update user status');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      await UserService.deleteUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete user');
    }
  }
);

export const searchUsers = createAsyncThunk(
  'users/searchUsers',
  async (searchTerm, { rejectWithValue }) => {
    try {
      const users = await UserService.searchUsers(searchTerm);
      return users;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to search users');
    }
  }
);

export const getUsersByStatus = createAsyncThunk(
  'users/getUsersByStatus',
  async (status, { rejectWithValue }) => {
    try {
      const users = await UserService.getUsersByStatus(status);
      return { status, users };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch users by status');
    }
  }
);

export const getUsersByRole = createAsyncThunk(
  'users/getUsersByRole',
  async (role, { rejectWithValue }) => {
    try {
      const users = await UserService.getUsersByRole(role);
      return { role, users };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch users by role');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    users: [],
    loading: false,
    error: null,
    selectedUsers: [],
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    suspendedUsers: 0,
    searchTerm: '',
    filters: {
      status: 'all',
      role: 'all'
    }
  },
  reducers: {
    setSelectedUsers: (state, action) => {
      state.selectedUsers = action.payload;
    },
    clearUsersError: (state) => {
      state.error = null;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilter: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.searchTerm = '';
      state.filters = { status: 'all', role: 'all' };
    },
    resetUsers: (state) => {
      state.users = [];
      state.selectedUsers = [];
      state.totalUsers = 0;
      state.activeUsers = 0;
      state.pendingUsers = 0;
      state.suspendedUsers = 0;
      state.searchTerm = '';
      state.filters = { status: 'all', role: 'all' };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
        state.totalUsers = action.payload.length;
        state.activeUsers = action.payload.filter(u => u.status === 'active').length;
        state.pendingUsers = action.payload.filter(u => u.status === 'pending').length;
        state.suspendedUsers = action.payload.filter(u => u.status === 'suspended').length;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update user status
      .addCase(updateUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, status } = action.payload;
        const oldStatus = state.users.find(u => u.id === userId)?.status;
        
        // Update user status
        state.users = state.users.map(user => 
          user.id === userId ? { ...user, status } : user
        );
        
        // Update counts
        if (oldStatus && oldStatus !== status) {
          // Decrement old status count
          if (oldStatus === 'active') state.activeUsers--;
          else if (oldStatus === 'pending') state.pendingUsers--;
          else if (oldStatus === 'suspended') state.suspendedUsers--;
          
          // Increment new status count
          if (status === 'active') state.activeUsers++;
          else if (status === 'pending') state.pendingUsers++;
          else if (status === 'suspended') state.suspendedUsers++;
        }
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        const userId = action.payload;
        const deletedUser = state.users.find(u => u.id === userId);
        
        if (deletedUser) {
          // Update counts
          if (deletedUser.status === 'active') state.activeUsers--;
          else if (deletedUser.status === 'pending') state.pendingUsers--;
          else if (deletedUser.status === 'suspended') state.suspendedUsers--;
          
          state.totalUsers--;
        }
        
        state.users = state.users.filter(user => user.id !== userId);
        state.selectedUsers = state.selectedUsers.filter(id => id !== userId);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Search users
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      
      // Get users by status
      .addCase(getUsersByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
      })
      
      // Get users by role
      .addCase(getUsersByRole.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
      });
  }
});

export const { 
  setSelectedUsers, 
  clearUsersError, 
  setSearchTerm, 
  setFilter, 
  clearFilters,
  resetUsers 
} = usersSlice.actions;

export default usersSlice.reducer;